import React, { useState } from 'react';
import './App.css';

// 新增函数：计算更亮的颜色
const getBrighterColor = (r, g, b, brightnessFactor = 1.2) => {
  // 将RGB转换为HSL
  const [h, s, l] = rgbToHsl(r, g, b);
  
  // 增加亮度，但不超过1.0
  const newL = Math.min(l * brightnessFactor, 1.0);
  
  // 转换回RGB
  const [newR, newG, newB] = hslToRgb(h, s, newL);
  
  return [newR, newG, newB];
};

// RGB转HSL函数
const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return [h, s, l];
};

// HSL转RGB函数
const hslToRgb = (h, s, l) => {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [r * 255, g * 255, b * 255];
};

const Tool04 = ({ toolProps = {} }) => {
  const {
    squareSize = 10,
    levels = 5,
    minBrightness = 0,
    maxBrightness = 255,
  } = toolProps;

  const [imageSrc, setImageSrc] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [hue, setHue] = useState(null);
  const [isBW, setIsBW] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [svgHue, setSvgHue] = useState(null);
  const [svgBrightnessFactor, setSvgBrightnessFactor] = useState(1.2);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;

        img.onload = () => {
          const width = img.width;
          const height = img.height;

          let newWidth = width;
          let newHeight = height;

          const maxWidth = window.innerWidth - 250 - 250 - 40 - 40 - 40;
          const maxHeight = window.innerHeight - 40 - 40 - 40 - 20;

          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (width > maxWidth) {
              newWidth = maxWidth;
              newHeight = newWidth / aspectRatio;
            }
            if (newHeight > maxHeight) {
              newHeight = maxHeight;
              newWidth = newHeight * aspectRatio;
            }
          }

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = newWidth;
          canvas.height = newHeight;
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          setImageSrc(canvas.toDataURL());
          setProcessedImage(null);
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const getLevelForBrightness = (brightness) => {
    const normalizedBrightness = Math.min(Math.max(brightness, minBrightness), maxBrightness);
    const range = maxBrightness - minBrightness;
    const step = range / levels;
    return Math.min(Math.floor((normalizedBrightness - minBrightness) / step), levels - 1);
  };

  const processImage = async () => {
    if (!imageSrc || isProcessing) return;
    setIsProcessing(true);

    try {
      const img = new Image();
      img.src = imageSrc;
      await new Promise((resolve) => (img.onload = resolve));

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      const halftoneCanvas = document.createElement('canvas');
      const halftoneCtx = halftoneCanvas.getContext('2d');
      halftoneCanvas.width = canvas.width;
      halftoneCanvas.height = canvas.height;

      const svgCanvas = document.createElement('canvas');
      const svgCtx = svgCanvas.getContext('2d');
      svgCanvas.width = canvas.width;
      svgCanvas.height = canvas.height;

      const loadPromises = [];

      for (let y = 0; y < canvas.height; y += squareSize) {
        for (let x = 0; x < canvas.width; x += squareSize) {
          let totalR = 0, totalG = 0, totalB = 0;
          let count = 0;

          for (let dy = 0; dy < squareSize && y + dy < canvas.height; dy++) {
            for (let dx = 0; dx < squareSize && x + dx < canvas.width; dx++) {
              const pixelIndex = ((y + dy) * canvas.width + (x + dx)) * 4;
              totalR += data[pixelIndex];
              totalG += data[pixelIndex + 1];
              totalB += data[pixelIndex + 2];
              count++;
            }
          }

          const avgR = totalR / count;
          const avgG = totalG / count;
          const avgB = totalB / count;
          const brightness = (avgR + avgG + avgB) / 3;

          // 处理方形格颜色
          let squareR = avgR;
          let squareG = avgG;
          let squareB = avgB;

          if (hue !== null && !isBW) {
            const [h, s, l] = rgbToHsl(avgR, avgG, avgB);
            const [newR, newG, newB] = hslToRgb(hue / 360, s, l);
            squareR = newR;
            squareG = newG;
            squareB = newB;
          } else if (isBW) {
            const gray = (avgR + avgG + avgB) / 3;
            squareR = squareG = squareB = gray;
          }

          // 绘制方形格
          halftoneCtx.fillStyle = `rgb(${squareR}, ${squareG}, ${squareB})`;
          halftoneCtx.fillRect(x, y, squareSize, squareSize);

          // 处理SVG颜色
          let svgR = squareR;
          let svgG = squareG;
          let svgB = squareB;

          // 应用亮度调整
          [svgR, svgG, svgB] = getBrighterColor(svgR, svgG, svgB, svgBrightnessFactor);

          // 应用SVG色相
          if (svgHue !== null) {
            const [h, s, l] = rgbToHsl(svgR, svgG, svgB);
            [svgR, svgG, svgB] = hslToRgb(svgHue / 360, s, l);
          }

          // 加载并绘制SVG
          const level = getLevelForBrightness(brightness);
          const svgPath = `/svg/level${level + 1}.svg`;
          
          const loadPromise = new Promise((resolve, reject) => {
            const svgImage = new Image();
            svgImage.onload = () => {
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = squareSize;
              tempCanvas.height = squareSize;
              const tempCtx = tempCanvas.getContext('2d');
              
              tempCtx.drawImage(svgImage, 0, 0, squareSize, squareSize);
              
              const imageData = tempCtx.getImageData(0, 0, squareSize, squareSize);
              const data = imageData.data;
              
              for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] > 0) {
                  data[i] = svgR;
                  data[i + 1] = svgG;
                  data[i + 2] = svgB;
                }
              }
              
              tempCtx.putImageData(imageData, 0, 0);
              svgCtx.drawImage(tempCanvas, x, y);
              resolve();
            };
            svgImage.onerror = reject;
            svgImage.src = svgPath;
          });

          loadPromises.push(loadPromise);
        }
      }

      await Promise.all(loadPromises);

      const finalCanvas = document.createElement('canvas');
      const finalCtx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      
      finalCtx.drawImage(halftoneCanvas, 0, 0);
      finalCtx.drawImage(svgCanvas, 0, 0);

      setProcessedImage(finalCanvas.toDataURL());
    } catch (error) {
      console.error('图像处理失败:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadImage = () => {
    if (!processedImage) return;
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = 'halftone-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="BWHalftone-container">
      <div className="canvas-panel-header">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          id="fileInput"
          style={{ display: 'none' }}
        />
        <label htmlFor="fileInput" className="custom-upload-btn">
          {imageSrc ? '更换图片' : '选择图片'}
        </label>

        {imageSrc && (
          <>
            <button 
              className="action-btn" 
              onClick={processImage}
              disabled={isProcessing}
            >
              {isProcessing ? '处理中...' : '生成效果'}
            </button>

            <button 
              className="action-btn" 
              onClick={() => setIsBW(!isBW)}
            >
              {isBW ? '切换彩色' : '切换黑白'}
            </button>

            {!isBW && (
              <input
                type="color"
                onChange={(e) => {
                  const hex = e.target.value;
                  const rgb = parseInt(hex.slice(1), 16);
                  setHue((rgb >> 16) & 0xff);
                }}
                style={{ width: '40px' }}
              />
            )}

            <div className="svg-controls">
              <label>
                <input
                  type="color"
                  onChange={(e) => {
                    const hex = e.target.value;
                    const rgb = parseInt(hex.slice(1), 16);
                    setSvgHue((rgb >> 16) & 0xff);
                  }}
                />
              </label>
              <button className="action-btn" onClick={() => setSvgHue(null)}>重置SVG色相</button>
            </div>

            {processedImage && (
              <button className="action-btn" onClick={downloadImage}>
                下载图片
              </button>
            )}
          </>
        )}
      </div>

      <div className="image-preview">
        {imageSrc && !processedImage && <img src={imageSrc} alt="原始图片" />}
        {processedImage && <img src={processedImage} alt="半调处理结果" />}
      </div>
    </div>
  );
};

export default Tool04;